// C.1 — Заражён (infected), keyword Лив Лайфа.
//
// Правило (диздок v2.9):
//   - Стак до 3. Накладывается Клодом и его картами.
//   - В начале каждого СВОЕГО хода (хода игрока, владеющего заражённым
//     юнитом) каждый заражённый юнит получает урон = стак (1=1, 2=2, 3=3).
//   - Снимается Молчанием (Молчание самого заражённого юнита).
//   - При смерти заражённого keyword не передаётся.
//   - После применения активки Клода (3 урона всем заражённым) — keyword
//     снимается со всех целей (removeInfectedFromAll).
//
// Хранение:
//   - card.infectedStacks (number, 0..3) — текущий стак.
//   - card.infectedSources (Set<string>) — источники (для отладки/трекинга).
//   - card.keywords содержит 'infected' если infectedStacks > 0.

import { KW } from '../keywords.js';
import { applyDamage } from '../combat.js';
// АНИМ.14-fix: ленивый импорт через dynamic require — onUnitDamaged/
// onUnitDied живут в abilities/deus/index.js и могут циклически зависеть.
// Без них death от Infected не триггерит Посмертия и сайдэффекты.

const KW_INFECTED = KW.INFECTED;
export const MAX_STACKS = 3;

// Повысить стак заражения у юнита. Возвращает фактический стак после
// применения (с учётом cap 3).
export function addInfectedStack(state, target, sourceId = 'infect', count = 1) {
  if (!target || target.type !== 'unit') return 0;
  if (!target.isAlive || !target.isAlive()) return 0;

  if (typeof target.infectedStacks !== 'number') target.infectedStacks = 0;
  if (!target.infectedSources) target.infectedSources = new Set();

  const before = target.infectedStacks;
  target.infectedStacks = Math.min(MAX_STACKS, before + Math.max(0, count));
  target.infectedSources.add(sourceId);

  // Поддерживаем keyword в массиве для UI.
  if (Array.isArray(target.keywords) && !target.keywords.includes(KW_INFECTED)) {
    target.keywords.push(KW_INFECTED);
  }
  if (target.current && !target.current.silenced) {
    // Молчание снимает keyword; иначе он виден.
  }

  if (state?.log && target.infectedStacks !== before) {
    const ownerName = state.players?.[target.ownerId]?.name || '?';
    state.log.add(
      `☣ ${target.name_ru} [${ownerName}]: Заражён ×${target.infectedStacks} (источник: ${sourceId})`
    );
  }
  return target.infectedStacks;
}

// Снять Заражение с одной цели (полностью, все стаки).
export function clearInfected(state, target) {
  if (!target) return false;
  if (!target.infectedStacks) return false;
  const had = target.infectedStacks;
  target.infectedStacks = 0;
  if (target.infectedSources) target.infectedSources.clear();
  if (Array.isArray(target.keywords)) {
    const idx = target.keywords.indexOf(KW_INFECTED);
    if (idx >= 0) target.keywords.splice(idx, 1);
  }
  if (state?.log && had > 0) {
    const ownerName = state.players?.[target.ownerId]?.name || '?';
    state.log.add(`☣ ${target.name_ru} [${ownerName}]: Заражение снято`);
  }
  return true;
}

// Снять Заражение со всех юнитов указанного игрока (или всех на поле,
// если ownerIdx=null).
export function removeInfectedFromAll(state, ownerIdx = null) {
  if (!state?.players) return 0;
  let removed = 0;
  const players = ownerIdx == null ? state.players : [state.players[ownerIdx]];
  for (const p of players) {
    if (!p?.field) continue;
    for (const row of p.field) {
      if (!row) continue;
      for (const u of row) {
        if (!u || !u.infectedStacks) continue;
        if (clearInfected(state, u)) removed++;
      }
    }
  }
  return removed;
}

// DoT в начале хода — наносит damage = стак каждому заражённому юниту
// игрока, чей сейчас ход. Возвращает массив { unit, damage } для лога.
export function tickInfectedDoT(state, ownerIdx) {
  if (!state?.players) return [];
  const p = state.players[ownerIdx];
  if (!p?.field) return [];

  const events = [];
  // Снимок ссылок — DoT не должен крашиться если кто-то умрёт в процессе.
  const targets = [];
  for (const row of p.field) {
    if (!row) continue;
    for (const u of row) {
      if (!u) continue;
      if (!u.isAlive || !u.isAlive()) continue;
      // Молчание снимает Заражённого — стак не тикает.
      if (u.current?.silenced) continue;
      if (!u.infectedStacks) continue;
      targets.push(u);
    }
  }
  for (const unit of targets) {
    const stacks = unit.infectedStacks || 0;
    if (stacks <= 0) continue;
    // Урон от Заражения — обычный (броня поглощает, нет пирса).
    // applyDamage эмитит триггер on_damage_taken и (если есть) Иммунитет.
    const result = applyDamage(unit, stacks, {
      state,
      source: { id: 'infected_dot', label: 'Заражение' }
    });
    events.push({ unit, damage: stacks, result });
    if (state?.log) {
      const ownerName = state.players?.[unit.ownerId]?.name || '?';
      state.log.add(`☣ ${unit.name_ru} [${ownerName}]: -${stacks} от Заражения`);
    }
    // АНИМ.14-fix: обработка смерти от DoT'а Заражения.
    // По аналогии с tickKustarnyyDoT — удаляем с поля.
    if (unit.current?.hp <= 0) {
      unit.alive = false;
      p.removeFromField(unit);
      if (state?.log) {
        const ownerName = state.players?.[unit.ownerId]?.name || '?';
        state.log.add(`✗ ${unit.name_ru} [${ownerName}] погибает (Заражение)`);
      }
      // Триггеры смерти (onUnitDied) вызываются caller'ом — мы возвращаем
      // events. Чтобы не плодить циклические импорты, caller (phases.js)
      // сам должен дёрнуть death-callback через onUnitDied если нужно.
    }
  }
  return events;
}

// Хук-чистильщик при Молчании цели: снять Заражение.
// Вызывать из silence.js applySilence (после очистки keywords).
export function clearInfectedOnSilence(state, target) {
  if (!target?.infectedStacks) return;
  clearInfected(state, target);
}
